import { Button, Input, Spinner } from '@crm/ui';
import { RotateCcw, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import type { Category } from '@/modules/catalog';
import { useCategories, useCreateCategory } from '@/modules/catalog';
import { useTemplates } from '@/modules/templates';
import { PhotoPicker } from './photo-picker';

interface Rascunho {
  templateId: string;
  name: string;
  photos: string[];
  indice: number;
}

/**
 * A lista de categorias pronta, ja preenchida - o salao so ajusta nome,
 * foto ou remove o que nao serve, e confirma. Cada categoria criada aqui
 * volta no mapa para o passo de servicos saber onde encaixar cada um.
 */
export function CategoriesStep({ onDone }: { onDone: (mapa: Record<string, string>) => void }) {
  const templates = useTemplates();
  const categoriasExistentes = useCategories({ page: 1, perPage: 100 });
  const createCategory = useCreateCategory();
  const [rascunhos, setRascunhos] = useState<Rascunho[] | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Trava sincrona: o `enviando` do state so vale no proximo render, e um
  // clique duplo rapido passa por ele. A ref barra na hora.
  const emAndamento = useRef(false);

  useEffect(() => {
    if (templates.data && rascunhos === null) {
      setRascunhos(
        templates.data.data.map((categoria) => ({
          templateId: categoria.id,
          name: categoria.name,
          photos: categoria.photos,
          indice: 0,
        })),
      );
    }
  }, [templates.data, rascunhos]);

  function reiniciar() {
    if (!templates.data) return;
    setRascunhos(
      templates.data.data.map((categoria) => ({
        templateId: categoria.id,
        name: categoria.name,
        photos: categoria.photos,
        indice: 0,
      })),
    );
  }

  async function continuar() {
    if (!rascunhos || emAndamento.current) return;

    emAndamento.current = true;
    setEnviando(true);
    setErro(null);

    // Categoria que ja existe (de uma visita anterior a este mesmo passo,
    // por exemplo) nao e recriada - so entra no mapa com o id que ja tem.
    const porNome = new Map(
      (categoriasExistentes.data?.data ?? []).map((categoria) => [
        categoria.name.trim().toLowerCase(),
        categoria.id,
      ]),
    );
    const mapa: Record<string, string> = {};

    try {
      for (const rascunho of rascunhos) {
        const chave = rascunho.name.trim().toLowerCase();
        const existente = porNome.get(chave);

        if (existente) {
          mapa[rascunho.templateId] = existente;
          continue;
        }

        const foto = rascunho.photos[rascunho.indice];
        const criada = (await createCategory.mutateAsync({
          name: rascunho.name,
          ...(foto ? { image: foto } : {}),
          isActive: true,
        })) as Category;

        porNome.set(chave, criada.id);
        mapa[rascunho.templateId] = criada.id;
      }

      onDone(mapa);
    } catch (error) {
      setErro(toErrorMessage(error));
    } finally {
      emAndamento.current = false;
      setEnviando(false);
    }
  }

  if (templates.isLoading || rascunhos === null) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        A lista mais comum de um salao. Ajuste nome, foto ou remova o que nao serve.
      </p>

      {erro ? <p className="text-sm text-destructive">{erro}</p> : null}

      {rascunhos.length === 0 ? (
        <p className="rounded-card border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          Nenhuma categoria - reinicie a lista ou crie direto em Categorias.
        </p>
      ) : (
        <div className="space-y-2">
          {rascunhos.map((rascunho, indice) => (
            <div
              key={rascunho.templateId}
              className="flex items-center gap-3 rounded-card border border-border bg-card p-2.5"
            >
              <PhotoPicker
                photos={rascunho.photos}
                indice={rascunho.indice}
                onChange={(proximo) =>
                  setRascunhos((atual) =>
                    atual!.map((item, i) => (i === indice ? { ...item, indice: proximo } : item)),
                  )
                }
              />

              <Input
                aria-label="Nome da categoria"
                className="flex-1"
                value={rascunho.name}
                onChange={(event) =>
                  setRascunhos((atual) =>
                    atual!.map((item, i) =>
                      i === indice ? { ...item, name: event.target.value } : item,
                    ),
                  )
                }
              />

              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remover ${rascunho.name}`}
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => setRascunhos((atual) => atual!.filter((_, i) => i !== indice))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={reiniciar}>
          <RotateCcw className="h-4 w-4" />
          Restaurar lista padrao
        </Button>

        <Button onClick={continuar} disabled={enviando || rascunhos.length === 0}>
          {enviando ? <Spinner className="h-4 w-4" /> : null}
          Continuar
        </Button>
      </div>
    </div>
  );
}
